import * as React from "react";
import { css } from "glamor";
import { Spin } from "antd";
import { BASE_URL } from "../../config";
import authFetch from "../../api/authFetch";
import { CommentItem } from "./Item";
import { CommentForm } from "./Form";
import { tagsToArray, buildTagsQuery } from "./utils";

interface Comment {
    content: string;
    id: number;
    parent?: Comment;
    room: string;
    timestamp: string;
    user: {
        id: number;
        username: number;
    };
}

interface CommentsProps {
    serverUrl: string;
    roomID: string | number;
    tags?: Array<string>;
}

interface CommentsState {
    comments: Array<Comment>;
    loading: boolean;
    creating: boolean;
}

const styles = {
    row: css({
        marginBottom: "1.5em",
    }),
    comment: css({
        marginTop: "-2px",
    }),
};

// eslint-disable-next-line no-undef
const COMMENTS_URL = `${BASE_URL}/flow-comments`;

export class Comments extends React.Component<CommentsProps, CommentsState> {
    state: CommentsState = {
        loading: true,
        creating: false,
        comments: [],
    };

    static defaultProps = {
        serverUrl: COMMENTS_URL,
    };

    private isLoaded = true;

    componentWillUnmount(): void {
        this.isLoaded = false;
    }

    async componentDidMount() {
        const { roomID, serverUrl, tags = {} } = this.props;
        const tagsQuery = buildTagsQuery(tags);

        this.isLoaded = true;

        const result = await authFetch(`${serverUrl}/${roomID}/?${tagsQuery}`);

        if (this.isLoaded) {
            await this.addComments(await result.json());
            this.setState({ loading: false });
        }
    }

    createComment = async (comment: string) => {
        const { roomID, serverUrl } = this.props;
        this.setState({ creating: true });

        const request = await authFetch(
            new Request(`${serverUrl}/${roomID}/`, {
                body: JSON.stringify({
                    content: comment,
                    tags: tagsToArray(this.props.tags || {}),
                }),
                headers: {
                    "Content-Type": "application/json",
                },
                method: "POST",
            })
        );
        const result = await request.json();

        if (this.isLoaded) {
            await this.addComments(result);
            this.setState({ creating: false });
        }
    };

    addComments = (comments: Comment | Array<Comment>) => {
        this.setState(prev => {
            return {
                comments: prev.comments.concat(comments),
            };
        });
    };

    render() {
        const { loading, comments, creating } = this.state;

        return (
            <Spin tip="Loading comments..." spinning={loading}>
                <div className={comments.length ? styles.row + "" : ""}>
                    {comments.map((comment, index) => (
                        <div
                            key={comment.id}
                            className={index > 0 ? styles.comment + "" : ""}
                        >
                            <CommentItem
                                name={comment.user.username}
                                timestamp={comment.timestamp}
                                content={comment.content}
                            />
                        </div>
                    ))}
                </div>
                <CommentForm
                    onSubmit={this.createComment}
                    isLoading={loading || creating}
                />
            </Spin>
        );
    }
}
